/**
 * <docs-contrast> -- WCAG and APCA contrast checker for brio token pairings.
 *
 * Renders a table showing each semantic foreground/background pairing
 * with both WCAG 2.1 contrast ratio (AA/AAA threshold) and APCA Lc
 * value (the perceptually-tuned contrast model proposed for WCAG 3).
 * Pairings come from contrast-pairs.mjs, shared with the CI token
 * check (scripts/check-contrast.mjs).
 *
 * Both are shown because they often disagree on borderline cases:
 * WCAG can rate dark-on-saturated as passing while APCA flags it as
 * weak (a known WCAG 2 weakness with mid-luminance saturated colors).
 * Conversely, some Radix-derived loud bgs pass APCA for body text but
 * only clear WCAG AA-large.
 *
 * Resolves current token values via a throwaway DOM element, so
 * results reflect whatever theme is active.
 *
 * Markup:
 *   <docs-contrast></docs-contrast>
 *
 * Scope: docs-only. Not bundled in brio.js. Lives in src/docs/ and is
 * registered from src/docs/docs.js.
 *
 * Re-renders when the <html> class attribute changes (theme switching),
 * so ratings stay accurate across dark-mode and themed views.
 *
 * APCA implementation: Bronze Simple Mode per APCA-W3
 * (https://github.com/Myndex/apca-w3). Returns signed Lc; magnitude
 * compared to thresholds (|Lc| >= 75 body text, >= 60 content,
 * >= 45 large/bold only, < 45 insufficient).
 */

import { BrioBase } from '../js/brio-base.js';
import { PAIRINGS } from './contrast-pairs.mjs';

/* --- APCA (Bronze Simple Mode) --------------------------------------------
 * Reference: APCA-W3 specification (https://github.com/Myndex/apca-w3).
 * Constants and formula match the Bronze Simple Mode -- the user-facing
 * implementation Radix and Adobe Spectrum cite.
 *
 * Returns signed Lc: positive for dark-text-on-light, negative for
 * light-text-on-dark. Magnitude is what's compared to thresholds.
 *
 * Declared above the class because the class's static block defines
 * the custom element synchronously on module load -- if a <docs-contrast>
 * is already in the DOM, connectedCallback fires before any const
 * declared after the class is initialized.
 */

const APCA_CONSTANTS = {
    sRGBtrc: 2.4,
    Rco: 0.2126729,
    Gco: 0.7151522,
    Bco: 0.072175,
    normBG: 0.56,
    normTXT: 0.57,
    revTXT: 0.62,
    revBG: 0.65,
    blkThrs: 0.022,
    blkClmp: 1.414,
    loClip: 0.1,
    deltaYmin: 0.0005,
    scaleBoW: 1.14,
    loBoWoffset: 0.027,
    scaleWoB: 1.14,
    loWoBoffset: 0.027,
};

function apcaSrgbToY(r, g, b) {
    const c = APCA_CONSTANTS;
    return (
        c.Rco * Math.pow(r / 255, c.sRGBtrc) +
        c.Gco * Math.pow(g / 255, c.sRGBtrc) +
        c.Bco * Math.pow(b / 255, c.sRGBtrc)
    );
}

function apcaContrast(textRGB, bgRGB) {
    const c = APCA_CONSTANTS;
    let txtY = apcaSrgbToY(textRGB[0], textRGB[1], textRGB[2]);
    let bgY = apcaSrgbToY(bgRGB[0], bgRGB[1], bgRGB[2]);

    // Soft clamp for very dark colors (avoid math discontinuities near black).
    if (txtY < c.blkThrs) txtY += Math.pow(c.blkThrs - txtY, c.blkClmp);
    if (bgY < c.blkThrs) bgY += Math.pow(c.blkThrs - bgY, c.blkClmp);

    if (Math.abs(bgY - txtY) < c.deltaYmin) return 0.0;

    let SAPC, output;
    if (bgY > txtY) {
        // Dark text on light bg ("BoW" polarity).
        SAPC = (Math.pow(bgY, c.normBG) - Math.pow(txtY, c.normTXT)) * c.scaleBoW;
        output = SAPC < c.loClip ? 0 : SAPC - c.loBoWoffset;
    } else {
        // Light text on dark bg ("WoB" polarity).
        SAPC = (Math.pow(bgY, c.revBG) - Math.pow(txtY, c.revTXT)) * c.scaleWoB;
        output = SAPC > -c.loClip ? 0 : SAPC + c.loWoBoffset;
    }

    return output * 100;
}

/**
 * Map |Lc| to a label per APCA Bronze guidelines:
 *   75+  body text (AAA-equivalent)
 *   60+  content text, 18px+ acceptable for body
 *   45+  large/bold only
 *   30+  large display text only
 *   <30  insufficient for any text
 */
function apcaRatingLabel(lc) {
    const mag = Math.abs(lc);
    if (mag >= 75) return 'Body';
    if (mag >= 60) return 'Content';
    if (mag >= 45) return 'Large';
    if (mag >= 30) return 'Display';
    return 'Fail';
}

function apcaRatingClass(lc) {
    const mag = Math.abs(lc);
    if (mag >= 60) return 'pass';
    if (mag >= 45) return 'warn';
    return 'fail';
}

class DocsContrast extends BrioBase {
    static tag = 'docs-contrast';
    static {
        customElements.define(DocsContrast.tag, DocsContrast);
    }

    #themeObserver;
    #render;

    connectedCallback() {
        this.#render = () => this.#renderTable();
        this.#render();

        // Re-render when the <html> class changes (dark-mode toggle,
        // theme switch). attributeFilter keeps us from firing on
        // unrelated mutations.
        this.#themeObserver = new MutationObserver(this.#render);
        this.#themeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });
    }

    disconnectedCallback() {
        if (this.#themeObserver) this.#themeObserver.disconnect();
    }

    #renderTable() {
        const rows = PAIRINGS.map((p) => this.#renderRow(p)).join('');
        this.innerHTML = `
            <div class="table-wrap">
                <table class="table is-compact">
                    <thead>
                        <tr>
                            <th>Pairing</th>
                            <th>Preview</th>
                            <th class="as-num">WCAG</th>
                            <th>WCAG rating</th>
                            <th class="as-num">APCA Lc</th>
                            <th>APCA rating</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        `;
    }

    #renderRow(p) {
        const fgRGB = resolveTokenRGB(p.fg);
        const bgRGB = resolveTokenRGB(p.bg);

        let ratio = '—';
        let wcagRating = '—';
        let wcagCls = '';
        let lc = '—';
        let apcaRating = '—';
        let apcaCls = '';

        if (fgRGB && bgRGB) {
            const fgL = luminance(fgRGB[0], fgRGB[1], fgRGB[2]);
            const bgL = luminance(bgRGB[0], bgRGB[1], bgRGB[2]);
            const min = p.min ?? 4.5;
            const cr = contrastRatio(fgL, bgL);
            ratio = `${cr.toFixed(2)}:1`;
            wcagRating = ratingLabel(cr, min);
            wcagCls = ratingClass(cr, min);

            const lcValue = apcaContrast(fgRGB, bgRGB);
            lc = `${lcValue >= 0 ? '+' : ''}${lcValue.toFixed(0)}`;
            apcaRating = apcaRatingLabel(lcValue);
            apcaCls = apcaRatingClass(lcValue);
        }

        const note =
            p.note && wcagCls !== 'pass'
                ? `<br><span class="text-mono text--1 text-subtle">${p.note}</span>`
                : '';

        return `
            <tr>
                <td><code>${p.label}</code>${note}</td>
                <td><span style="background:var(${p.bg});color:var(${p.fg});padding:0.15em 0.5em;border-radius:var(--radius-sm);border:1px solid var(--color-border-subtle);">Sample</span></td>
                <td class="as-num">${ratio}</td>
                <td><span class="badge ${badgeVariant(wcagCls)}">${wcagRating}</span></td>
                <td class="as-num">${lc}</td>
                <td><span class="badge ${badgeVariant(apcaCls)}">${apcaRating}</span></td>
            </tr>
        `;
    }
}

/* --- Color parsing and luminance ------------------------------------------
 * Browsers may return resolved colors in rgb(), rgba(), color(srgb ...),
 * color(display-p3 ...), or oklch() depending on the author's input and
 * the browser version. We handle each form; for anything else, fall back
 * to a canvas round-trip which always produces sRGB.
 */

function parseColor(str) {
    if (!str) return null;
    str = str.trim();

    const rgbMatch = str.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/);
    if (rgbMatch) {
        return [parseFloat(rgbMatch[1]), parseFloat(rgbMatch[2]), parseFloat(rgbMatch[3])];
    }

    const srgbMatch = str.match(/^color\(srgb\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)/);
    if (srgbMatch) {
        return [
            clamp01(parseFloat(srgbMatch[1])) * 255,
            clamp01(parseFloat(srgbMatch[2])) * 255,
            clamp01(parseFloat(srgbMatch[3])) * 255,
        ];
    }

    const p3Match = str.match(/^color\(display-p3\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)/);
    if (p3Match) {
        return [
            clamp01(parseFloat(p3Match[1])) * 255,
            clamp01(parseFloat(p3Match[2])) * 255,
            clamp01(parseFloat(p3Match[3])) * 255,
        ];
    }

    if (str.startsWith('oklch(') || str.startsWith('oklch (')) {
        return resolveViaCanvas(str);
    }

    return null;
}

function clamp01(n) {
    return Math.max(0, Math.min(1, n));
}

function resolveViaCanvas(colorStr) {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = colorStr;
    ctx.fillRect(0, 0, 1, 1);
    const data = ctx.getImageData(0, 0, 1, 1).data;
    return [data[0], data[1], data[2]];
}

// WCAG relative luminance from sRGB channel (0-255).
function luminance(r, g, b) {
    const channel = (c) => {
        const n = c / 255;
        return n <= 0.04045 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrastRatio(l1, l2) {
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Resolve a CSS custom property to [r, g, b] in 0-255 range.
 * Uses a real DOM element so custom properties inherit from :root and
 * any scoped overrides in the current subtree. The element is removed
 * immediately after reading.
 */
function resolveTokenRGB(token) {
    const el = document.createElement('div');
    el.style.position = 'absolute';
    el.style.visibility = 'hidden';
    el.style.backgroundColor = `var(${token})`;
    document.body.appendChild(el);
    const computed = getComputedStyle(el).backgroundColor;
    el.remove();

    const rgb = parseColor(computed);
    if (rgb) return rgb;
    return resolveViaCanvas(computed);
}

function ratingLabel(ratio, min = 4.5) {
    if (min < 4.5) return ratio >= min ? 'Pass' : 'Fail';
    if (ratio >= 7) return 'AAA';
    if (ratio >= 4.5) return 'AA';
    if (ratio >= 3) return 'AA-lg';
    return 'Fail';
}

function ratingClass(ratio, min = 4.5) {
    if (ratio >= min) return 'pass';
    if (ratio >= 3) return 'warn';
    return 'fail';
}

function badgeVariant(cls) {
    if (cls === 'pass') return 'is-success';
    if (cls === 'warn') return 'is-warning';
    if (cls === 'fail') return 'is-danger';
    return 'is-neutral';
}
